import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiGithub, FiGitCommit, FiCalendar, FiExternalLink } from "react-icons/fi";

function GitHubActivity() {
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGitHubCommits();
  }, []);

  const fetchGitHubCommits = async () => {
    try {
      const response = await fetch('http://localhost:8001/api/github/commits');
      if (!response.ok) {
        throw new Error('Failed to fetch commits');
      }
      const data = await response.json();
      setCommits(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} days ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} weeks ago`;
  };

  if (loading) {
    return (
      <div className="glass-card p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-neutral-medium">Loading GitHub activity...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-8">
        <div className="text-center text-neutral-medium">
          <FiGithub className="mx-auto mb-4" size={48} />
          <p>Unable to load GitHub activity at the moment.</p>
          <p className="text-sm mt-2">Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="glass-card p-8"
    >
      <div className="flex items-center mb-6">
        <div className="p-3 bg-primary/10 text-primary rounded-xl mr-4">
          <FiGithub size={24} />
        </div>
        <div>
          <h3 className="text-xl font-bold">Latest GitHub Activity</h3>
          <p className="text-neutral-medium text-sm">Recent commits across my repositories</p>
        </div>
      </div>

      {commits.length === 0 ? (
        <div className="text-center text-neutral-medium py-8">
          <FiGitCommit className="mx-auto mb-4" size={48} />
          <p>No recent commits found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {commits.slice(0, 5).map((commit, index) => (
            <motion.div
              key={`${commit.repository}-${commit.sha}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-l-2 border-primary/20 pl-4 pb-4 relative group hover:border-primary/40 transition-colors"
            >
              <div className="absolute -left-2 top-1 w-3 h-3 bg-primary rounded-full"></div>

              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                      {commit.sha}
                    </span>
                    <span className="text-sm font-medium text-primary">
                      {commit.repository}
                    </span>
                  </div>

                  <p className="text-neutral-dark font-medium text-sm mb-2 line-clamp-2">
                    {commit.message}
                  </p>

                  <div className="flex items-center text-xs text-neutral-medium gap-4">
                    <span className="flex items-center">
                      <FiCalendar className="mr-1" />
                      {formatDate(commit.date)}
                    </span>
                    <span className="text-neutral-light">•</span>
                    <span>{formatTimeAgo(commit.date)}</span>
                  </div>
                </div>

                <a
                  href={commit.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-4 p-2 hover:bg-primary/10 rounded-lg"
                  title="View commit on GitHub"
                >
                  <FiExternalLink size={16} className="text-primary" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-100">
        <a
          href="https://github.com/GenaAiv"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center text-primary hover:text-primary-dark transition-colors"
        >
          <FiGithub className="mr-2" />
          View all repositories on GitHub
          <FiExternalLink className="ml-2" size={14} />
        </a>
      </div>
    </motion.div>
  );
}

export default GitHubActivity;